import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { previewCart as previewCartApi } from "./api";
import { trackAddToCart, trackRemoveFromCart } from "./analytics";

const CartContext = createContext(null);
const STORAGE_KEY = "platebuddies_cart_v1";

function loadCart() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return {
      items: Array.isArray(raw.items) ? raw.items : [],
      coupon: typeof raw.coupon === "string" ? raw.coupon : "",
      // Campaign gate — recovery/upsell links carry these so private
      // coupons (COMEBACK15, AGAIN10) can be unlocked at checkout.
      recoveryToken: typeof raw.recoveryToken === "string" ? raw.recoveryToken : "",
      recoverySessionId: typeof raw.recoverySessionId === "string" ? raw.recoverySessionId : "",
    };
  } catch (err) {
    console.warn("[cart] Failed to parse stored cart, resetting.", err);
    return { items: [], coupon: "", recoveryToken: "", recoverySessionId: "" };
  }
}

function persist(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn("[cart] Failed to persist cart.", err);
  }
}

export function CartProvider({ children }) {
  const _initial = loadCart();
  const [items, setItems] = useState(_initial.items);
  const [coupon, setCoupon] = useState(_initial.coupon);
  const [recoveryToken, setRecoveryToken] = useState(_initial.recoveryToken);
  const [recoverySessionId, setRecoverySessionId] = useState(_initial.recoverySessionId);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    persist({ items, coupon, recoveryToken, recoverySessionId });
  }, [items, coupon, recoveryToken, recoverySessionId]);

  // Global auto-apply: any URL landing (Shop, Home, ProductDetail, Cart, /free-buddy)
  // with `?coupon=CODE` (or `?promo=CODE`) captures the code into cart state
  // before the visitor even reaches /cart. Private coupons additionally
  // require `rt` (recovery token) + `sid` (session id) to unlock server-side.
  // Runs once on mount and again on history navigation (popstate) — safe to
  // no-op if a coupon is already set.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const capture = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const raw = (params.get("coupon") || params.get("promo") || "").trim().toUpperCase();
        const rt = (params.get("rt") || "").trim();
        const sid = (params.get("sid") || "").trim();
        if (raw) {
          setCoupon((curr) => (curr ? curr : raw));
        }
        // Capture rt+sid even if a coupon was already set — recovery emails
        // may open days after a shopper started a session, and we want the
        // token to unlock the code they already have in state.
        if (rt) setRecoveryToken(rt);
        if (sid) setRecoverySessionId(sid);
      } catch {
        /* ignore */
      }
    };
    capture();
    window.addEventListener("popstate", capture);
    return () => window.removeEventListener("popstate", capture);
  }, []);

  // Server-side preview (subtotal / discount / total)
  useEffect(() => {
    if (items.length === 0) {
      setPreview(null);
      return;
    }
    const slim = items.map(({ product_id, quantity, custom_buddy_id, custom_buddy_name }) => ({
      product_id,
      quantity,
      custom_buddy_id,
      custom_buddy_name,
    }));
    let cancelled = false;
    previewCartApi(slim, coupon || undefined, {
      recovery_token: recoveryToken || undefined,
      recovery_session_id: recoverySessionId || undefined,
    })
      .then((d) => {
        if (!cancelled) setPreview(d);
      })
      .catch(() => !cancelled && setPreview(null));
    return () => {
      cancelled = true;
    };
  }, [items, coupon, recoveryToken, recoverySessionId]);

  const itemCount = items.reduce((s, i) => s + (i.quantity || 1), 0);

  const addItem = useCallback((item) => {
    // Fire GA4 add_to_cart before mutating state so the event carries the
    // clicked item's price/qty, not the merged post-state.
    trackAddToCart(item);
    setItems((curr) => {
      // For personalized buddy prints, treat as unique by custom_buddy_id.
      const matchIdx = curr.findIndex(
        (c) =>
          c.product_id === item.product_id &&
          (c.custom_buddy_id || null) === (item.custom_buddy_id || null)
      );
      if (matchIdx >= 0) {
        const next = [...curr];
        next[matchIdx] = {
          ...next[matchIdx],
          quantity: Math.min((next[matchIdx].quantity || 1) + (item.quantity || 1), 10),
        };
        return next;
      }
      return [...curr, { quantity: 1, ...item }];
    });
  }, []);

  const updateQty = useCallback((idx, delta) => {
    setItems((curr) => {
      const next = [...curr];
      const q = (next[idx].quantity || 1) + delta;
      if (q <= 0) {
        trackRemoveFromCart(next[idx]);
        return next.filter((_, i) => i !== idx);
      }
      next[idx] = { ...next[idx], quantity: Math.min(q, 10) };
      return next;
    });
  }, []);

  const removeItem = useCallback((idx) => {
    setItems((curr) => {
      if (curr[idx]) trackRemoveFromCart(curr[idx]);
      return curr.filter((_, i) => i !== idx);
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setCoupon("");
    setRecoveryToken("");
    setRecoverySessionId("");
  }, []);

  return (
    <CartContext.Provider
      value={{
        items,
        coupon,
        setCoupon,
        addItem,
        updateQty,
        removeItem,
        clearCart,
        preview,
        itemCount,
        recoveryToken,
        recoverySessionId,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
